import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppStore } from "../store/useAppStore";
import { Chip } from "../components/ui/Chip";
import { DesktopHeaderBar } from "../components/layout/AppShell";

const statusTone = {
  occupied: "success",
  vacant: "operational",
  turnover: "attention",
  "leased-pending-move-in": "brand"
} as const;

const statusLabel = {
  occupied: "Occupied",
  vacant: "Vacant",
  turnover: "Turnover",
  "leased-pending-move-in": "Pending move-in"
} as const;

export function Units() {
  const activeSiteId = useAppStore((s) => s.demo.activeSiteId);
  const units = useAppStore((s) => s.units);
  const tenants = useAppStore((s) => s.tenants);
  const situations = useAppStore((s) => s.situations);
  const [query, setQuery] = useState("");

  const siteUnits = useMemo(() => units.filter((u) => u.siteId === activeSiteId), [units, activeSiteId]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return siteUnits;
    return siteUnits.filter((u) => {
      const tenant = tenants.find((t) => t.id === u.tenantId);
      return u.unitNumber.toLowerCase().includes(q) || tenant?.companyName.toLowerCase().includes(q);
    });
  }, [siteUnits, tenants, query]);

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <DesktopHeaderBar />
      <div>
        <h1 className="font-display text-[24px] font-black tracking-tight text-brand-navy">Units</h1>
        <p className="text-[13px] text-muted-foreground">{siteUnits.length} units at this site</p>
      </div>
      <label className="relative block">
        <span className="sr-only">Search units or tenant</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search unit number or tenant…"
          className="w-full rounded-sm border border-border bg-card px-3.5 py-2.5 text-[14px] text-foreground placeholder:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        />
      </label>
      <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((unit) => {
          const tenant = tenants.find((t) => t.id === unit.tenantId);
          const openSituations = situations.filter((s) => s.unitId === unit.id && s.status === "open").length;
          return (
            <li key={unit.id}>
              <Link
                to={`/units/${unit.id}`}
                className="flex touch-target flex-col gap-1.5 rounded-md border border-border bg-card p-3.5 shadow-elevation-1 hover:border-primary"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[13px] font-semibold text-brand-navy">{unit.unitNumber}</span>
                  <Chip tone={statusTone[unit.status]}>{statusLabel[unit.status]}</Chip>
                </div>
                <p className="font-action text-[14px] font-bold leading-5 text-foreground">{tenant?.companyName ?? "Vacant"}</p>
                <p className="text-[12px] text-muted-foreground">
                  {unit.buildingLabel} · {unit.kind} · {unit.sqft.toLocaleString()} sq ft
                </p>
                {openSituations > 0 && <p className="mt-1 font-mono text-[10.5px] uppercase tracking-wide text-attention">{openSituations} open situation{openSituations === 1 ? "" : "s"}</p>}
              </Link>
            </li>
          );
        })}
      </ul>
      {filtered.length === 0 && <p className="rounded-md border border-dashed border-border p-6 text-center text-[13.5px] text-muted-foreground">No units match "{query}".</p>}
    </div>
  );
}
